import jsPDF from "jspdf";
import QRCode from "qrcode";

/**
 * Piece tag PDF generator (Premier plant).
 *
 * Two tags per page, side by side, split by a vertical perforation. Each tag
 * is portrait-shaped (taller than wide). The QR sits at the TOP of the tag;
 * the text block below it is rotated 90° CW, so the printed tag is read by
 * holding it portrait-style.
 *
 *  Page 8 × 8 in (203.2 × 203.2 mm), two 4-in tag slots
 *  ┌──────────────────────┬──────────────────────┐
 *  │       [QR top]       │       [QR top]       │
 *  │                      │                      │
 *  │   BCH-403A (rotated) │   BCH-403A (rotated) │
 *  │   3083               │   3082               │
 *  │   Job:Fat Village .. │   Job:Fat Village .. │
 *  └──────────────────────┴──────────────────────┘
 *  x=0                   101.6                  203.2 mm
 *                  perforation (vertical)
 *
 * GEOMETRY IS UNVERIFIED AGAINST PHYSICAL STOCK (2026-08-17). Earlier revisions
 * of this file carried three mutually contradictory claims in comments — "6 × 4
 * in landscape", "6 × 8 in portrait", and a "3-inch perforation" — none of which
 * matched the constants below. They have been removed rather than reconciled,
 * because none of them was measured. TAG_STOCK is now the single source of
 * truth: change it here and every derived value follows.
 *
 * Until Josh confirms the real roll dimensions, correctness does not depend on
 * these numbers being right: fitBlockToSafeWidth() below measures the text block
 * against TAG_SAFE_W_MM and shrinks it to fit, so the tag adapts to whatever
 * stock is loaded instead of silently printing content off the edge.
 */

const MM_PER_INCH = 25.4;

/**
 * Physical tag stock. The ONLY place these dimensions are declared.
 * `ripOffIn` is the inner strip next to the perforation that gets torn away,
 * so nothing may be drawn inside it.
 */
const TAG_STOCK = {
  pageIn: 8,     // page is square: 8 in × 8 in
  tagsPerPage: 2,
  ripOffIn: 0.4,
} as const;

const PAGE_W_MM = TAG_STOCK.pageIn * MM_PER_INCH;              // 203.2
const PAGE_H_MM = TAG_STOCK.pageIn * MM_PER_INCH;              // 203.2
const TAG_GROSS_W_MM = PAGE_W_MM / TAG_STOCK.tagsPerPage;      // 101.6 (one tag = 4" wide)
const RIP_OFF_W_MM = TAG_STOCK.ripOffIn * MM_PER_INCH;         // 10.16 (inner rip toward perforation)
export const TAG_SAFE_W_MM = TAG_GROSS_W_MM - RIP_OFF_W_MM;    // ~91.44 — the hard budget

/** Breathing room kept clear at each edge of the safe zone. */
const EDGE_PAD_MM = 1.0;

/**
 * Font-size floors used when the block has to shrink. The identity lines (piece
 * type, mark number) stay legible across the yard; the detail lines may go
 * smaller because they are read in the hand.
 */
const MIN_IDENTITY_PT = 18;
const MIN_DETAIL_PT = 9;

// QR code occupies the top of the tag, centered horizontally.
const QR_SIZE_MM = 80;             // ~3.15 in
const QR_TOP_INSET_MM = 3;         // gap from page top to QR
const QR_TO_TEXT_GAP_MM = 4;       // gap below QR before text block starts

export interface PieceTagInput {
  /** The string the QR encodes (typically uniquePieceIdentifier) */
  qrPayload: string;
  /** Big text on the tag. Typically panelMark or short identifier. */
  markNumber: string;
  /** Project number/name shown under the mark */
  jobNumber: string;
  /** Mold instance code */
  moldInstanceCode: string;
  /** Panel type (e.g., "BCH-403A") */
  panelType?: string;
  /** Location / elevation (e.g., "West Elevation") */
  location?: string;
  /** Building level / floor (e.g., "L3") */
  level?: string;
  /** Color name (e.g., "Mystique Sand") */
  color?: string;
  /** Finish description (e.g., "Acid Etch") */
  finish?: string;
  /** Volume in cubic feet — string-formatted for display */
  volumeCuft?: string;
  /** Volume in cubic yards — string-formatted for display */
  volumeCuyd?: string;
  /** Weight in pounds — string-formatted for display */
  weightLbs?: string;
}

interface InternalDrawOptions {
  /** Left X of the tag's gross 3-inch slot (0 for tag 1, 76.2 for tag 2) */
  slotLeft: number;
  /** Whether the safe zone hugs the outer (page) edge (tag 1=left edge, tag 2=right edge). */
  safeOnOuterEdge: 'left' | 'right';
}

/**
 * Build a multi-page PDF with two tags per page (left + right).
 * If `pieces` length is odd, the last page has the second slot blank.
 */
export async function generatePieceTagPdf(pieces: PieceTagInput[]): Promise<jsPDF> {
  if (pieces.length === 0) {
    throw new Error("generatePieceTagPdf: no pieces provided");
  }

  // Page is taller than wide (6" × 8") → portrait. Earlier 4"-tall versions
  // were landscape; the orientation flips as height grows past the 6" width.
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: [PAGE_W_MM, PAGE_H_MM] });

  for (let i = 0; i < pieces.length; i += 2) {
    if (i > 0) doc.addPage([PAGE_W_MM, PAGE_H_MM], "portrait");

    await drawTag(doc, pieces[i], { slotLeft: 0, safeOnOuterEdge: 'left' });

    if (i + 1 < pieces.length) {
      await drawTag(doc, pieces[i + 1], { slotLeft: TAG_GROSS_W_MM, safeOnOuterEdge: 'right' });
    }

    // Faint perforation guide (won't print on a perforated roll)
    doc.setDrawColor(200);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(TAG_GROSS_W_MM, 0, TAG_GROSS_W_MM, PAGE_H_MM);
    doc.setLineDashPattern([], 0);
    doc.setDrawColor(0);
  }

  return doc;
}

export interface TagLine {
  value: string;
  fontSize: number;
  bold: boolean;
  prefix?: string;
  /** Identity lines (piece type, mark number) shrink less aggressively. */
  identity?: boolean;
}

/**
 * Build the tag's reading-order line list. Pure — no jsPDF, no side effects.
 *
 * Reading order is push order (see the rotation note in drawTag), so the list
 * below is literally top-to-bottom on the printed tag:
 *
 *   PIECE TYPE      <- 9pt label
 *   BCH-403A        <- 30pt value, the field crews read across the yard
 *   MARK NUMBER     <- 9pt label
 *   3083            <- 28pt value
 *   Job: ...        <- 15pt details
 *
 * Josh, 2026-08-13: "put this piece type up and label piece type, because I put
 * that label in there" and "I like the label onto the mark number... push that
 * to the side". Commit 197d088 made the type large but dropped its "Type:"
 * prefix and left it BELOW the mark, so the labelling half of the ask was never
 * delivered. Both labels are now their own small column above their value —
 * an inline 28pt/30pt prefix would eat the ~112mm per-line budget and truncate
 * real Premier marks such as "A201/1-01-2".
 */
export function buildTagLines(piece: PieceTagInput): TagLine[] {
  const lines: TagLine[] = [];

  // --- Identity block: piece type first, then mark number. Both always render,
  // with an em-dash placeholder when absent. Rendering the type unconditionally
  // is deliberate: when the source record lacked panelType the line used to
  // disappear entirely, which on paper is indistinguishable from "the sizing fix
  // was never applied" — exactly what Josh reported on 08-13. A visible "—"
  // says "the data is missing", which is a different and more useful complaint.
  lines.push({ value: "PIECE TYPE", fontSize: 9, bold: true, identity: true });
  lines.push({ value: piece.panelType || "—", fontSize: 30, bold: true, identity: true });
  lines.push({ value: "MARK NUMBER", fontSize: 9, bold: true, identity: true });
  lines.push({ value: piece.markNumber || "—", fontSize: 28, bold: true, identity: true });

  // --- Detail block. Job and Mold always render (they identify the piece);
  // the rest are dropped when blank. Previously location/level/color/finish were
  // pushed unconditionally with an em-dash, burning 28.8mm of a 91.44mm budget
  // on four lines that said nothing — while the numeric lines below were already
  // gated. That inconsistency is what pushed the block over the safe width.
  lines.push({ value: piece.jobNumber || "—", fontSize: 15, bold: false, prefix: "Job" });
  lines.push({ value: piece.moldInstanceCode || "—", fontSize: 15, bold: false, prefix: "Mold" });
  if (piece.location) lines.push({ value: piece.location, fontSize: 15, bold: false, prefix: "Location" });
  if (piece.level) lines.push({ value: piece.level, fontSize: 15, bold: false, prefix: "Level" });
  if (piece.color) lines.push({ value: piece.color, fontSize: 15, bold: false, prefix: "Color" });
  if (piece.finish) lines.push({ value: piece.finish, fontSize: 15, bold: false, prefix: "Finish" });

  // Cu Yd is intentionally NOT printed: it is derived (cuft / 27, see
  // buildPieceTagInput) so it carries no information the Cu Ft line lacks, and
  // dropping it reclaims 7.2mm of a budget the crew's Weight line was losing to.
  if (piece.volumeCuft) lines.push({ value: piece.volumeCuft, fontSize: 15, bold: false, prefix: "Cu Ft" });
  if (piece.weightLbs) lines.push({ value: piece.weightLbs, fontSize: 15, bold: false, prefix: "Weight" });

  return lines;
}

/**
 * Lay the lines out as columns and GUARANTEE the block fits the tag.
 *
 * Each column's horizontal width on the page ≈ font size in mm (1pt ≈ 0.353mm,
 * with ascender+descender taking ~0.9 * fontSize total). 0.4 is a tight visual
 * approximation that keeps columns close without overlapping.
 *
 * The fit pass is the fix for Josh's 08-13 report that the weight was cut off.
 * Before it, `totalBlockWidth` was computed and then never compared against
 * TAG_SAFE_W_MM: a fully-populated piece measured 94.00mm against a 91.44mm
 * budget, the block was centred, and the last column drawn — Weight — landed at
 * a negative page X and simply vanished. There was no horizontal clamp anywhere
 * in the file; the only clamp was vertical, on individual line length.
 *
 * Shrinking is proportional and only engages when the block actually exceeds
 * the budget, so tags that already fit are byte-identical to before.
 */
export function computeTagBlock(input: TagLine[]): {
  lines: TagLine[];
  columnGap: number;
  columnWidths: number[];
  totalBlockWidth: number;
  scale: number;
} {
  const columnGap = 1.2;
  const budget = TAG_SAFE_W_MM - 2 * EDGE_PAD_MM;

  const widthOf = (lns: TagLine[]) =>
    lns.reduce((sum, l) => sum + l.fontSize * 0.4, 0) + columnGap * (lns.length - 1);

  let lines = input;
  let totalBlockWidth = widthOf(lines);
  let scale = 1;

  if (totalBlockWidth > budget) {
    // Solve for the scale that fits, accounting for the gaps being fixed.
    const gapTotal = columnGap * (lines.length - 1);
    const textTotal = totalBlockWidth - gapTotal;
    scale = Math.max(0, (budget - gapTotal) / textTotal);

    lines = input.map((l) => ({
      ...l,
      fontSize: Math.max(
        l.identity ? Math.min(MIN_IDENTITY_PT, l.fontSize) : Math.min(MIN_DETAIL_PT, l.fontSize),
        l.fontSize * scale,
      ),
    }));
    totalBlockWidth = widthOf(lines);
  }

  return {
    lines,
    columnGap,
    columnWidths: lines.map((l) => l.fontSize * 0.4),
    totalBlockWidth,
    scale,
  };
}

async function drawTag(doc: jsPDF, piece: PieceTagInput, opts: InternalDrawOptions): Promise<void> {
  // Safe zone X range — tag 1 hugs left page edge, tag 2 hugs right page edge
  const safeX0 = opts.safeOnOuterEdge === 'left'
    ? opts.slotLeft
    : opts.slotLeft + RIP_OFF_W_MM;
  const safeCenterX = safeX0 + TAG_SAFE_W_MM / 2;

  // === QR at the top, centered ===
  const qrPayload = piece.qrPayload || piece.markNumber || "panel";
  const qrDataUrl = await QRCode.toDataURL(qrPayload, { width: 256, margin: 1 });
  const qrX = safeCenterX - QR_SIZE_MM / 2;
  const qrY = QR_TOP_INSET_MM;
  doc.addImage(qrDataUrl, "PNG", qrX, qrY, QR_SIZE_MM, QR_SIZE_MM);

  // === Rotated text block below the QR ===
  // Each line is drawn with angle = -90 (90° CW) so the chars' tops point to
  // the RIGHT (matching the customer's reference tag — you tilt the tag CCW /
  // left to read). With -90 the text advances DOWNWARD from the anchor, so the
  // anchor lives at the top of the available text area (no QR overlap).
  // Lines are parallel COLUMNS across the page: mark on the right, job in the
  // middle, mold on the left. After the 90° rotation when reading, mark
  // becomes the TOP line and mold the BOTTOM line — they stack underneath
  // each other in the reading view.
  const textTop = qrY + QR_SIZE_MM + QR_TO_TEXT_GAP_MM;
  const textBottomBound = PAGE_H_MM - 4;

  // Build the list of lines top-to-bottom (mark first → bottom-most info last).
  // Optional fields are dropped from the list when blank so the layout stays
  // tight when not all data is present.
  // Build the line list, then fit it to the tag BEFORE measuring — the fit pass
  // may shrink font sizes, and the measurement has to reflect the sizes actually
  // drawn. computeTagBlock is pure and exported so the layout can be unit-tested
  // without a jsPDF instance (see generatePieceTag.test.ts).
  const { lines, columnGap, columnWidths, totalBlockWidth } = computeTagBlock(buildTagLines(piece));

  // Measure each line in the un-rotated frame. The text "width" becomes the
  // vertical extent of the column on the page after the 90° rotation.
  const measured: Array<{ text: string; width: number; fontSize: number; bold: boolean }> = [];
  for (const ln of lines) {
    doc.setFont("helvetica", ln.bold ? "bold" : "normal");
    doc.setFontSize(ln.fontSize);
    const fullText = ln.prefix ? `${ln.prefix}: ${ln.value}` : ln.value;
    const w = doc.getTextWidth(fullText);
    measured.push({ text: fullText, width: w, fontSize: ln.fontSize, bold: ln.bold });
  }

  // Center the whole text block horizontally under the QR.
  // Piece-type column sits at the RIGHT edge of the block, the last detail
  // line at the LEFT.
  let columnRightEdge = safeCenterX + totalBlockWidth / 2;
  const sharedAnchorY = textTop;

  // Belt-and-braces: computeTagBlock should have guaranteed the fit, so this
  // can only fire if someone adds a line type it doesn't know how to shrink.
  // Warn loudly in dev rather than silently printing off the edge again.
  const blockLeftEdge = columnRightEdge - totalBlockWidth;
  if (blockLeftEdge < safeX0 || columnRightEdge > safeX0 + TAG_SAFE_W_MM) {
    console.warn(
      `[generatePieceTag] block (${totalBlockWidth.toFixed(2)}mm) does not fit the ` +
        `${TAG_SAFE_W_MM.toFixed(2)}mm safe zone for mark "${piece.markNumber}" — ` +
        `content may print off the tag.`,
    );
  }

  for (let i = 0; i < measured.length; i++) {
    const m = measured[i];
    doc.setFont("helvetica", m.bold ? "bold" : "normal");
    doc.setFontSize(m.fontSize);

    // Anchor (baseline-left of first char in original frame). With angle = -90,
    // the character body extends RIGHT of anchor by ~0.7 * fontSize (ascender)
    // and LEFT by ~0.2 * fontSize (descender). Center the column visually:
    const columnCenter = columnRightEdge - columnWidths[i] / 2;
    const anchorX = columnCenter - m.fontSize * 0.25;

    // Clamp Y so a long line doesn't run off the bottom of the page.
    const drawY = sharedAnchorY;
    if (drawY + m.width > textBottomBound) {
      const maxWidth = textBottomBound - drawY;
      const truncated = truncateForWidth(doc, m.text, maxWidth);
      // Josh's requirement is that every line item is present and complete, so
      // a silent ellipsis is a defect we want visible in QA rather than
      // discovered on a printed tag.
      console.warn(
        `[generatePieceTag] line truncated on mark "${piece.markNumber}": ` +
          `"${m.text}" -> "${truncated}"`,
      );
      doc.text(truncated, anchorX, drawY, { angle: -90 });
    } else {
      doc.text(m.text, anchorX, drawY, { angle: -90 });
    }

    columnRightEdge -= columnWidths[i] + columnGap;
  }
}

function truncateForWidth(doc: jsPDF, text: string, maxW: number): string {
  if (doc.getTextWidth(text) <= maxW) return text;
  let s = text;
  while (s.length > 1 && doc.getTextWidth(s + "…") > maxW) {
    s = s.slice(0, -1);
  }
  return s.length > 1 ? s + "…" : s;
}

/**
 * Print a single piece — equivalent to a one-piece batch.
 * Saves the PDF with a suggested filename.
 */
export async function printSinglePieceTag(piece: PieceTagInput): Promise<void> {
  const doc = await generatePieceTagPdf([piece]);
  doc.save(`${piece.markNumber || piece.qrPayload}-tag.pdf`);
}

/**
 * Print a batch — saves a multi-page PDF.
 */
export async function printBatchPieceTags(
  pieces: PieceTagInput[],
  filename = `piece-tags-${pieces.length}.pdf`
): Promise<void> {
  if (pieces.length === 0) return;
  const doc = await generatePieceTagPdf(pieces);
  doc.save(filename);
}

// ---------------------------------------------------------------------------
// PieceTagInput builder — single source of truth for converting a panel-shaped
// object into the PieceTagInput consumed by the PDF generator.
//
// Both the Master Piece List (uses DataGrid rows in camelCase) and the
// Production Schedule slideout (uses raw panel records that may carry
// snake_case keys) call this. Adding a new field to the tag means updating
// PieceTagInput above and this mapper — both call sites then pick it up
// automatically.
// ---------------------------------------------------------------------------

function fmtNum(value: unknown, suffix = ""): string | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return undefined;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

export function buildPieceTagInput(source: Record<string, unknown>): PieceTagInput {
  const pick = (...keys: string[]): unknown => {
    for (const k of keys) {
      const v = source[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
  };

  const idSeed = pick("id", "panelId", "panel_id");
  const identifier =
    (pick("uniquePieceIdentifier", "unique_piece_identifier") as string) ||
    (pick("panelMark", "panel_mark") as string) ||
    `Panel-${idSeed ?? ""}`;

  const panelMark =
    (pick("panelMark", "panel_mark") as string) || identifier;

  const projectId = pick("projectId", "project_id");
  const jobNumber =
    (pick("projectNumber", "project_number") as string) ||
    (pick("projectName", "project_name") as string) ||
    (projectId ? `Project ${projectId}` : "") ||
    "";

  const cuftRaw = pick("volumeCuft", "volume_cuft");
  const cuft = typeof cuftRaw === "number" ? cuftRaw : Number(cuftRaw);
  const cuyd = Number.isFinite(cuft) ? cuft / 27 : NaN;

  return {
    qrPayload: identifier,
    markNumber: panelMark,
    jobNumber,
    moldInstanceCode:
      (pick("moldInstanceCode", "mold_instance_code") as string) || "",
    panelType: (pick("panelType", "panel_type") as string) || undefined,
    location: (pick("location") as string) || undefined,
    level: (pick("level") as string) || undefined,
    color: (pick("color") as string) || undefined,
    finish: (pick("finish") as string) || undefined,
    volumeCuft: Number.isFinite(cuft) ? fmtNum(cuft) : undefined,
    volumeCuyd: Number.isFinite(cuyd) ? fmtNum(cuyd) : undefined,
    weightLbs: fmtNum(pick("weightLbs", "weight_lbs"), " lbs"),
  };
}
